import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ApiLicense {
  id?: string;
  type: string;
  issued: string;
  expiry: string;
  issuer?: string;
}

export interface ApiTraining {
  id?: string;
  name: string;
  date: string;
  expiry?: string | null;
  status: string;
}

export interface ApiDriver {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  authPin?: string;          // write-only on create/update; never returned
  hasAuthPin?: boolean;      // returned: whether a PIN is set (hash not exposed)
  dob?: string;
  address?: string;
  since?: string;
  status: string;
  employment: string;
  hoursWeek?: number | null;
  vehicleId?: string | null;
  vacationTotal: number;
  vacationUsed: number;
  sickDaysYtd: number;
  emergencyName?: string;
  emergencyRelation?: string;
  emergencyPhone?: string;
  avatarPhoto?: string | null;
  tripsJan: number; tripsFeb: number; tripsMar: number;
  tripsApr: number; tripsMai: number; tripsJun: number;
  punctuality: number;
  avgTripMin: number;
  kmYtd: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  licenses: ApiLicense[];
  trainings: ApiTraining[];
}

export type DriverCreateDto = Omit<ApiDriver, 'id' | 'isActive' | 'createdAt' | 'updatedAt'>;
export type DriverUpdateDto = Partial<DriverCreateDto>;

/** A trip row in a driver's trip history. */
export interface DriverTrip {
  id: string;
  tripNumber: string;
  status: string;
  scheduledAt: string;
  returnScheduledAt?: string | null;
  patientName: string;
  patientFirstName?: string | null;
  patientLastName?: string | null;
  pickupAddress: string;
  dropoffAddress: string;
  distanceKm?: number | null;
  vehicle?: { licensePlate: string } | null;
  /* route-map fields — enough for the trip map modal (planned + driven route) */
  pickupLat?: number | null;
  pickupLng?: number | null;
  dropoffLat?: number | null;
  dropoffLng?: number | null;
  routePolyline?: string | null;
  attributes?: Record<string, any> | null;
  dropoffCustomer?: { name: string } | null;
}

/** One driver's last known position (GET /drivers/live-positions). */
export interface DriverLivePosition {
  driverId: string;
  name: string;
  phone?: string | null;
  status: string;
  vehiclePlate?: string | null;
  lat: number;
  lng: number;
  lastLocationAt: string;
  /** Age of the fix in seconds at response time. */
  ageSec: number;
  /** Younger than the server's freshness window (15 min) — the driver is live. */
  fresh: boolean;
  /** The in-motion trip the driver is on right now, if any. */
  trip?: { id: string; tripNumber: string; status: string } | null;
}
export interface DriverLivePositionsResponse {
  positions: DriverLivePosition[];
}

/** One driver's live location incl. recent trail (GET /drivers/:id/location).
 *  `position` is null when no GPS fix was ever written through for them. */
export interface DriverLocation {
  driverId: string;
  name: string;
  phone?: string | null;
  status: string;
  vehiclePlate?: string | null;
  position: {
    lat: number;
    lng: number;
    lastLocationAt: string;
    ageSec: number;
    fresh: boolean;
  } | null;
  trip?: { id: string; tripNumber: string; status: string; pickupAddress?: string; dropoffAddress?: string } | null;
  /** Recent fixes, oldest first — drawn as the trail behind the live marker. */
  trail: { lat: number; lng: number; recordedAt: string }[];
  trailHours: number;
}

/** A driver ranked by distance from a pickup (GET /drivers/nearby). */
export interface DriverNearby {
  driverId: string;
  name: string;
  phone?: string | null;
  status: string;
  vehicleId?: string | null;
  vehiclePlate?: string | null;
  vehicleType?: string | null;
  lat: number;
  lng: number;
  lastLocationAt: string;
  ageSec: number;
  fresh: boolean;
  /** Straight-line km from the pickup — not a routed distance. */
  distanceKm: number;
  /** Rough minutes at a 30 km/h city average; shown with a "≈". */
  etaMin: number;
  trip?: { id: string; tripNumber: string; status: string } | null;
  /** On duty and not already driving a trip. */
  available: boolean;
}
export interface DriverNearbyResponse {
  origin: { lat: number; lng: number } | null;
  drivers: DriverNearby[];
  /** 'NO_PICKUP_COORDS' when the trip has no geocoded pickup to rank against. */
  reason?: string;
}

@Injectable({ providedIn: 'root' })
export class DriverService {
  private readonly base = `${environment.apiUrl}/drivers`;

  constructor(private http: HttpClient) {}

  private nocache() {
    return {
      headers: new HttpHeaders({ 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }),
      params: { _t: Date.now().toString() },
    };
  }

  list(): Observable<ApiDriver[]> {
    return this.http.get<ApiDriver[]>(this.base, this.nocache());
  }

  get(id: string): Observable<ApiDriver> {
    return this.http.get<ApiDriver>(`${this.base}/${id}`, this.nocache());
  }

  /** Trip history for a driver (trips assigned to this driver record). */
  trips(id: string): Observable<DriverTrip[]> {
    return this.http.get<DriverTrip[]>(`${this.base}/${id}/trips`, this.nocache());
  }

  /** Last known position per active driver — feeds the Karte's driver layer. */
  livePositions(): Observable<DriverLivePositionsResponse> {
    return this.http.get<DriverLivePositionsResponse>(`${this.base}/live-positions`, this.nocache());
  }

  /** One driver's live location + recent trail — the detail page's Location tab. */
  location(id: string, trailHours?: number): Observable<DriverLocation> {
    const { headers, params } = this.nocache();
    return this.http.get<DriverLocation>(`${this.base}/${id}/location`, {
      headers,
      params: { ...params, ...(trailHours ? { trailHours: String(trailHours) } : {}) },
    });
  }

  /** Drivers closest to a pickup, nearest first — the assign dialog's strip.
   *  Pass a tripId (uses that trip's pickup) or explicit coordinates. */
  nearby(opts: { tripId?: string; lat?: number; lng?: number; limit?: number }): Observable<DriverNearbyResponse> {
    const { headers, params } = this.nocache();
    const q: Record<string, string> = { ...params };
    if (opts.tripId) q['tripId'] = opts.tripId;
    if (opts.lat != null) q['lat'] = String(opts.lat);
    if (opts.lng != null) q['lng'] = String(opts.lng);
    if (opts.limit) q['limit'] = String(opts.limit);
    return this.http.get<DriverNearbyResponse>(`${this.base}/nearby`, { headers, params: q });
  }

  create(dto: DriverCreateDto): Observable<ApiDriver> {
    return this.http.post<ApiDriver>(this.base, dto);
  }

  update(id: string, dto: DriverUpdateDto): Observable<ApiDriver> {
    return this.http.put<ApiDriver>(`${this.base}/${id}`, dto);
  }

  patchStatus(id: string, status: string): Observable<ApiDriver> {
    return this.http.patch<ApiDriver>(`${this.base}/${id}/status`, { status });
  }

  remove(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }

  // Licenses
  getLicenses(driverId: string): Observable<ApiLicense[]> {
    return this.http.get<ApiLicense[]>(`${this.base}/${driverId}/licenses`, this.nocache());
  }
  createLicense(driverId: string, dto: Omit<ApiLicense, 'id'>): Observable<ApiLicense> {
    return this.http.post<ApiLicense>(`${this.base}/${driverId}/licenses`, dto);
  }
  updateLicense(driverId: string, licId: string, dto: Omit<ApiLicense, 'id'>): Observable<ApiLicense> {
    return this.http.put<ApiLicense>(`${this.base}/${driverId}/licenses/${licId}`, dto);
  }
  deleteLicense(driverId: string, licId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${driverId}/licenses/${licId}`);
  }

  // Trainings
  getTrainings(driverId: string): Observable<ApiTraining[]> {
    return this.http.get<ApiTraining[]>(`${this.base}/${driverId}/trainings`, this.nocache());
  }
  createTraining(driverId: string, dto: Omit<ApiTraining, 'id'>): Observable<ApiTraining> {
    return this.http.post<ApiTraining>(`${this.base}/${driverId}/trainings`, dto);
  }
  updateTraining(driverId: string, trainId: string, dto: Omit<ApiTraining, 'id'>): Observable<ApiTraining> {
    return this.http.put<ApiTraining>(`${this.base}/${driverId}/trainings/${trainId}`, dto);
  }
  deleteTraining(driverId: string, trainId: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${driverId}/trainings/${trainId}`);
  }
}
